import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, Button, Spin, message, Table, Tooltip, Select } from 'antd';
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchActivitiesByLocation, fetchPlanVersions } from './api';

const { RangePicker } = DatePicker;

// CSS Styles matching the dashboard design
const styles = {
  container: {
    padding: '24px',
    fontFamily: "'DM Sans', -apple-system, sans-serif",
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '13px',
    color: '#718096',
    marginTop: '4px',
  },
  headerMeta: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    fontSize: '14px',
    color: '#4a5568',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#ebf8ff',
    border: '1px solid #90cdf4',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '13px',
    color: '#2c5282',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    background: '#3182ce',
    borderRadius: '50%',
    animation: 'pulse 2s infinite',
  },
    filters: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '12px 16px',
    // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    // marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    // border: '1px solid #e2e8f0',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  timeSlicers: {
    display: 'flex',
    gap: '4px',
    padding: '2px',
    background: '#f7fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  timeBtn: {
    padding: '4px 10px',
    border: 'none',
    background: 'transparent',
    color: '#718096',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
  },
  timeBtnActive: {
    background: '#2c5282',
    color: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  btnExport: {
    marginLeft: 'auto',
    padding: '8px 16px',
    background: '#2c5282',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    height: '36px',
  },
  btnSecondary: {
    padding: '8px 16px',
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#4a5568',
    height: '36px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  kpiCard: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '14px 16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    position: 'relative',
    overflow: 'hidden',
  },
  kpiCardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',

  },
  kpiHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  kpiTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kpiIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  kpiValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    lineHeight: 1,
    marginBottom: '6px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  kpiSubtitle: {
    fontSize: '12px',
    color: '#4a5568',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  progressBar: {
    height: '4px',
    background: '#f7fafc',
    borderRadius: '2px',
    overflow: 'hidden',
    marginTop: '8px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.6s ease',
  },
  tableCard: {
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  tableHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  tableActions: {
    display: 'flex',
    gap: '12px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: "'JetBrains Mono', monospace",
  },
  badgeSuccess: {
    background: '#c6f6d5',
    color: '#22543d',
  },
  badgeDanger: {
    background: '#fed7d7',
    color: '#742a2a',
  },
  badgeNeutral: {
    background: '#e2e8f0',
    color: '#4a5568',
  },
};

const CleaningTimeVariance = () => {
  // Calculate default date/time range dynamically
  const calculateDefaultRange = () => {
    const now = new Date();
    const rounded = new Date(now);
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    const end = dayjs(rounded);
    const start = end.subtract(23, 'hour');
    return [start, end];
  };

  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [activeTimeRange, setActiveTimeRange] = useState(null); // Quick time range selector

  // Plan version state
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(null); // null means latest
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Handle quick time range selection
  const handleTimeRangeChange = (range) => {
    setActiveTimeRange(range);
    const now = dayjs();
    let start;
    switch (range) {
      case '1D':
        start = now.subtract(1, 'day');
        break;
      case '1W':
        start = now.subtract(1, 'week');
        break;
      case '1M':
        start = now.subtract(1, 'month');
        break;
      case '3M':
        start = now.subtract(3, 'month');
        break;
      case '6M':
        start = now.subtract(6, 'month');
        break;
      default:
        return;
    }
    setDateTimeRange([start, now]);
  };

  // Calculate duration in hours between two dates
  const calculateDurationHours = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return null;
    if (endDateStr === 'running') return null;
    const start = dayjs(startDateStr);
    const end = dayjs(endDateStr);
    if (!start.isValid() || !end.isValid()) return null;
    const diffMs = end.diff(start);
    if (diffMs < 0) return null;
    const hours = diffMs / (1000 * 60 * 60);
    return hours;
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '-';
    return hours.toFixed(2);
  };

  // Get equipment category from name
  const getAreaFromEquipmentName = (equipmentName) => {
    if (!equipmentName) return 'Other';
    const name = String(equipmentName).toUpperCase();
    if (name.startsWith('COMP-') || name.startsWith('COMP')) return 'Compression';
    if (name.startsWith('COAT-') || name.startsWith('COAT')) return 'Coating';
    if (name.startsWith('CAPS-') || name.startsWith('CAPS')) return 'Capsulation';
    if (name.startsWith('IMP-') || name.startsWith('IMP')) return 'Imprinting';
    if (name.startsWith('FBP-') || name.startsWith('FBP')) return 'FBP';
    if (/^P\d+\s*(PRIMARY|SECONDARY)$/.test(name)) return 'Packing';
    if (name.match(/^B\d/)) return 'Blending';
    if (name.match(/^G\d/)) return 'Granulation';
    return 'Other';
  };

  // Check if activity is cleaning
  const isCleaningActivity = (activity) => {
    const activityType = (activity.activity_type || '').toLowerCase();
    const planActivity = (activity.plan_activity || '').toLowerCase();
    return activityType === 'cleaning' || planActivity.includes('cleaning') || planActivity.includes('clean');
  };

  // Get cleaning type from activity
  const getCleaningType = (activity) => {
    // Try to extract cleaning type from plan_activity or actual_activity
    const planActivity = activity.plan_activity || '';
    const actualActivity = activity.actual_activity || '';
    
    // Check for Type-A, Type-B, Type-C patterns
    const typeMatch = (planActivity + ' ' + actualActivity).match(/type[-\s]?([a-z])/i);
    if (typeMatch) {
      return `Type-${typeMatch[1].toUpperCase()}`;
    }
    
    // Check for specific cleaning type keywords
    if (planActivity.toLowerCase().includes('major') || actualActivity.toLowerCase().includes('major')) {
      return 'Major Cleaning';
    }
    if (planActivity.toLowerCase().includes('minor') || actualActivity.toLowerCase().includes('minor')) {
      return 'Minor Cleaning';
    }
    
    // Return the activity name if available
    return planActivity || actualActivity || 'Cleaning';
  };

  // Process API data into table rows - aggregated by Machine + Cleaning Type
  const processData = useCallback((apiData) => {
    if (!apiData?.data) return [];

    // First pass: collect all cleaning activities and aggregate by machine + type
    const aggregated = new Map(); // key: "machine|cleaningType"

    Object.entries(apiData.data).forEach(([equipmentName, equipmentData]) => {
      const eqpSequence = equipmentData.eqp_sequence ?? 9999;
      const allActivities = [
        ...(equipmentData.matched || []),
        ...(equipmentData.not_started || []),
        ...(equipmentData.unplanned || [])
      ];
      const cleaningActivities = allActivities.filter(isCleaningActivity);

      cleaningActivities.forEach((activity) => {
        const cleaningType = getCleaningType(activity);
        const planDurationHrs = calculateDurationHours(activity.plan_start_date, activity.plan_end_date);
        const actualDurationHrs = calculateDurationHours(activity.actual_start_time, activity.actual_end_time);
        
        const key = `${equipmentName}|${cleaningType}`;
        
        if (!aggregated.has(key)) {
          aggregated.set(key, {
            machine: equipmentName,
            area: getAreaFromEquipmentName(equipmentName),
            eqpSequence: activity.eqp_sequence ?? eqpSequence,
            cleaningType,
            planDuration: 0,
            actualDuration: 0,
            hasPlanData: false,
            hasActualData: false,
          });
        }
        
        const item = aggregated.get(key);
        if (planDurationHrs !== null) {
          item.planDuration += planDurationHrs;
          item.hasPlanData = true;
        }
        if (actualDurationHrs !== null) {
          item.actualDuration += actualDurationHrs;
          item.hasActualData = true;
        }
      });
    });

    // Convert to array and calculate differences
    const rows = [];
    let rowKey = 0;
    aggregated.forEach((item) => {
      const planDuration = item.hasPlanData ? item.planDuration : null;
      const actualDuration = item.hasActualData ? item.actualDuration : null;
      
      let difference = null;
      if (planDuration !== null && actualDuration !== null) {
        difference = actualDuration - planDuration;
      }
      
      // Only add rows that have at least one duration value
      if (planDuration !== null || actualDuration !== null) {
        rows.push({
          key: rowKey++,
          machine: item.machine,
          area: item.area,
          eqpSequence: item.eqpSequence,
          cleaningType: item.cleaningType,
          planDuration,
          actualDuration,
          difference,
        });
      }
    });

    // Sort by equipment sequence, then by cleaning type
    rows.sort((a, b) => {
      if (a.eqpSequence !== b.eqpSequence) return a.eqpSequence - b.eqpSequence;
      return a.cleaningType.localeCompare(b.cleaningType);
    });
    return rows;
  }, []);

  // Calculate KPI statistics
  const kpiStats = useMemo(() => {
    // Helper to check if value is valid (not null/empty/"-")
    const isValidValue = (val) => val !== null && val !== undefined && val !== '-';
    
    // TILE 1: Total Cleanings Planned - where Plan Duration has value
    const plannedRecords = tableData.filter(r => isValidValue(r.planDuration));
    const totalPlanned = plannedRecords.length;
    const plannedTypeA = plannedRecords.filter(r => r.cleaningType === 'Type-A').length;
    const plannedTypeC = plannedRecords.filter(r => r.cleaningType === 'Type-C').length;
    
    // TILE 2: Total Cleanings Completed - where Actual Duration has value
    const completedRecords = tableData.filter(r => isValidValue(r.actualDuration));
    const totalCompleted = completedRecords.length;
    const completedTypeA = completedRecords.filter(r => r.cleaningType === 'Type-A').length;
    const completedTypeC = completedRecords.filter(r => r.cleaningType === 'Type-C').length;
    
    // TILE 3: On-Time Completion Performance
    // Overall: Difference <= 0
    const onTimeRecords = completedRecords.filter(r => isValidValue(r.difference) && r.difference <= 0);
    const onTimeTotal = onTimeRecords.length;
    const onTimePct = totalCompleted > 0 ? ((onTimeTotal / totalCompleted) * 100).toFixed(1) : '0.0';
    
    // Type-A on-time
    const completedTypeARecords = completedRecords.filter(r => r.cleaningType === 'Type-A');
    const onTimeTypeA = completedTypeARecords.filter(r => isValidValue(r.difference) && r.difference <= 0).length;
    const onTimeTypeAPct = completedTypeA > 0 ? ((onTimeTypeA / completedTypeA) * 100).toFixed(1) : '0.0';
    
    // Type-C on-time
    const completedTypeCRecords = completedRecords.filter(r => r.cleaningType === 'Type-C');
    const onTimeTypeC = completedTypeCRecords.filter(r => isValidValue(r.difference) && r.difference <= 0).length;
    const onTimeTypeCPct = completedTypeC > 0 ? ((onTimeTypeC / completedTypeC) * 100).toFixed(1) : '0.0';
    
    // TILE 4: Avg Duration Variance - Type A
    const typeAWithDiff = completedTypeARecords.filter(r => isValidValue(r.difference));
    const avgVarianceTypeA = typeAWithDiff.length > 0
      ? typeAWithDiff.reduce((sum, r) => sum + r.difference, 0) / typeAWithDiff.length
      : 0;
    
    // TILE 5: Avg Duration Variance - Type C
    const typeCWithDiff = completedTypeCRecords.filter(r => isValidValue(r.difference));
    const avgVarianceTypeC = typeCWithDiff.length > 0
      ? typeCWithDiff.reduce((sum, r) => sum + r.difference, 0) / typeCWithDiff.length
      : 0;

    return { 
      // Tile 1
      totalPlanned,
      plannedTypeA,
      plannedTypeC,
      // Tile 2
      totalCompleted,
      completedTypeA,
      completedTypeC,
      // Tile 3
      onTimeTotal,
      onTimePct,
      onTimeTypeAPct,
      onTimeTypeCPct,
      // Tile 4 & 5
      avgVarianceTypeA: avgVarianceTypeA.toFixed(1),
      avgVarianceTypeC: avgVarianceTypeC.toFixed(1),
      typeACount: typeAWithDiff.length,
      typeCCount: typeCWithDiff.length
    };
  }, [tableData]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      
      // Build API params - only include plan_version if a specific version is selected
      const apiParams = {
        start_date: startDate,
        end_date: endDate
      };
      if (selectedPlanVersion !== null) {
        apiParams.plan_version = selectedPlanVersion;
      }
      
      const response = await fetchActivitiesByLocation(apiParams);
      if (response?.success) {
        const processed = processData(response);
        setTableData(processed);
      } else {
        message.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [dateTimeRange, selectedPlanVersion, processData]);

  // Export to Excel
  const handleExportExcel = async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setExporting(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      const headers = ['Machine', 'Cleaning Type', 'Plan Duration (hrs)', 'Actual Duration (hrs)', 'Difference (hrs)'];
      const csvRows = [
        headers.join(','),
        ...tableData.map(row => [
          row.machine,
          row.cleaningType,
          formatHours(row.planDuration),
          formatHours(row.actualDuration),
          formatHours(row.difference)
        ].map(cell => `"${cell}"`).join(','))
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cleaning_time_variance_${startDate.split(' ')[0]}_to_${endDate.split(' ')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      message.error('Error exporting data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (dateTimeRange && dateTimeRange.length === 2) {
      fetchData();
    }
  }, [dateTimeRange, selectedPlanVersion]);

  // Fetch plan versions on mount
  useEffect(() => {
    const loadPlanVersions = async () => {
      setLoadingVersions(true);
      try {
        const response = await fetchPlanVersions();
        if (response?.success && response.data) {
          setPlanVersions(response.data);
        }
      } catch (error) {
        console.error('Error loading plan versions:', error);
      } finally {
        setLoadingVersions(false);
      }
    };
    loadPlanVersions();
  }, []);

  // Table columns
  const columns = [
    {
      title: 'Machine',
      dataIndex: 'machine',
      key: 'machine',
      width: 180,
      fixed: 'left',
      filters: [
        { text: 'Granulation', value: 'Granulation' },
        { text: 'FBP', value: 'FBP' },
        { text: 'Blending', value: 'Blending' },
        { text: 'Compression', value: 'Compression' },
        { text: 'Coating', value: 'Coating' },
        { text: 'Capsulation', value: 'Capsulation' },
        { text: 'Imprinting', value: 'Imprinting' },
        { text: 'Packing', value: 'Packing' },
        { text: 'Other', value: 'Other' }
      ],
      onFilter: (value, record) => record.area === value,
      sorter: (a, b) => a.eqpSequence - b.eqpSequence,
      defaultSortOrder: 'ascend',
      render: (value, record) => <span style={{ fontWeight: '600' }}>{record.area} - {value}</span>
    },
    {
      title: 'Cleaning Type',
      dataIndex: 'cleaningType',
      key: 'cleaningType',
      width: 180,
      filters: [
        { text: 'Type-A', value: 'Type-A' },
        { text: 'Type-B', value: 'Type-B' },
        { text: 'Type-C', value: 'Type-C' },
        { text: 'Major Cleaning', value: 'Major Cleaning' },
        { text: 'Minor Cleaning', value: 'Minor Cleaning' },
      ],
      onFilter: (value, record) => record.cleaningType === value,
    },
    {
      title: 'Plan Duration',
      dataIndex: 'planDuration',
      key: 'planDuration',
      width: 140,
      align: 'right',
      render: (value) => formatHours(value),
      sorter: (a, b) => (a.planDuration || 0) - (b.planDuration || 0),
    },
    {
      title: 'Actual Duration',
      dataIndex: 'actualDuration',
      key: 'actualDuration',
      width: 150,
      align: 'right',
      render: (value) => formatHours(value),
      sorter: (a, b) => (a.actualDuration || 0) - (b.actualDuration || 0),
    },
    {
      title: 'Difference',
      dataIndex: 'difference',
      key: 'difference',
      width: 140,
      align: 'center',
      sorter: (a, b) => (a.difference || 0) - (b.difference || 0),
      render: (value) => {
        if (value === null || value === undefined) return '-';
        const isNegative = value < 0;
        const isZero = value === 0;
        return (
          <span style={{
            ...styles.badge,
            ...(isZero ? styles.badgeNeutral : isNegative ? styles.badgeSuccess : styles.badgeDanger)
          }}>
            {isNegative ? '' : '+'}{value.toFixed(2)}
          </span>
        );
      }
    }
  ];

  const dateRangeDisplay = dateTimeRange && dateTimeRange.length === 2
    ? `${dateTimeRange[0].format('DD MMM')} - ${dateTimeRange[1].format('DD MMM YYYY')}`
    : 'Select Date Range';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center'}}>
            <div>
          <h1 style={styles.headerTitle}>Cleaning Time Variance</h1>
          <div style={styles.headerSubtitle}>
            Analysis of planned vs actual cleaning durations
          </div></div>
          <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date Range</label>
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={dateTimeRange}
            onChange={(dates) => {
              setDateTimeRange(dates);
              setActiveTimeRange(null);
            }}
            style={{ minWidth: '340px' }}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Quick Select</label>
          <div style={styles.timeSlicers}>
            {['1D', '1W', '1M', '3M', '6M'].map((range) => (
              <button
                key={range}
                style={{
                  ...styles.timeBtn,
                  ...(activeTimeRange === range ? styles.timeBtnActive : {})
                }}
                onClick={() => handleTimeRangeChange(range)}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Plan Version</label>
          <Select
            value={selectedPlanVersion}
            onChange={(value) => setSelectedPlanVersion(value)}
            loading={loadingVersions}
            style={{ minWidth: '180px' }}
            placeholder="Select version"
          >
            <Select.Option value={null}>Latest Version</Select.Option>
            {planVersions.map((v) => (
              <Select.Option key={v.plan_version} value={v.plan_version}>
                v{v.plan_version} - {dayjs(v.published_at).format('DD MMM HH:mm')}
              </Select.Option>
            ))}
          </Select>
        </div>
        {/* <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          loading={loading}
          style={styles.btnSecondary}
        >
          Refresh
        </Button> */}
      </div>
        </div>
        <div style={styles.headerMeta}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: '600', color: '#1a202c' }}>{dateRangeDisplay}</div>
            <div style={{ fontSize: '12px' }}>Analysis Period</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        {/* TILE 1: Total Cleanings Planned */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: 'linear-gradient(90deg, #3182ce, #2c5282)' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Total Cleanings Planned</div>
            <Tooltip title="Formula: COUNT(records where Plan Duration has a value). Split by cleaning type.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.totalPlanned}</div>
          <div style={styles.kpiSubtitle}>
            <span style={{ color: '#6366f1' }}>{kpiStats.plannedTypeA} Type-A</span>
            <span style={{ margin: '0 8px', color: '#718096' }}>|</span>
            <span style={{ color: '#f59e0b' }}>{kpiStats.plannedTypeC} Type-C</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '100%', background: 'linear-gradient(90deg, #3182ce, #2c5282)' }}></div>
          </div>
        </div>

        {/* TILE 2: Total Cleanings Completed */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: '#38a169' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Total Cleanings Completed</div>
            <Tooltip title="Formula: COUNT(records where Actual Duration has a value). Split by cleaning type.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.totalCompleted}</div>
          <div style={styles.kpiSubtitle}>
            <span style={{ color: '#6366f1' }}>{kpiStats.completedTypeA} Type-A</span>
            <span style={{ margin: '0 8px', color: '#718096' }}>|</span>
            <span style={{ color: '#f59e0b' }}>{kpiStats.completedTypeC} Type-C</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: kpiStats.totalPlanned > 0 ? `${(kpiStats.totalCompleted / kpiStats.totalPlanned * 100)}%` : '0%', background: '#38a169' }}></div>
          </div>
        </div>

        {/* TILE 3: On-Time Completion % */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: parseFloat(kpiStats.onTimePct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimePct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>On-Time Completion %</div>
            <Tooltip title="Formula: (Cleanings with Difference ≤ 0 / Total Completed) × 100. Difference ≤ 0 means completed within or faster than planned duration. Calculated separately for each cleaning type.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.onTimePct}%</div>
          <div style={styles.kpiSubtitle}>
            <span style={{ color: '#6366f1' }}>Type-A: {kpiStats.onTimeTypeAPct}%</span>
            <span style={{ margin: '0 8px', color: '#718096' }}>|</span>
            <span style={{ color: '#f59e0b' }}>Type-C: {kpiStats.onTimeTypeCPct}%</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${kpiStats.onTimePct}%`, background: parseFloat(kpiStats.onTimePct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimePct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          </div>
        </div>

        {/* TILE 4: Avg Duration Variance - Type A */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: parseFloat(kpiStats.avgVarianceTypeA) <= 0 ? '#38a169' : '#e53e3e' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Avg Variance - Type A</div>
            <Tooltip title="Formula: AVG(Difference) for Type-A cleanings. Negative value = completed faster than planned. Positive value = took longer than planned. Based on completed cleanings only.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={{ ...styles.kpiValue, color: parseFloat(kpiStats.avgVarianceTypeA) <= 0 ? '#38a169' : '#e53e3e' }}>
            {parseFloat(kpiStats.avgVarianceTypeA) > 0 ? '+' : ''}{kpiStats.avgVarianceTypeA} hrs
          </div>
          <div style={styles.kpiSubtitle}>Based on {kpiStats.typeACount} Type-A cleanings</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '100%', background: parseFloat(kpiStats.avgVarianceTypeA) <= 0 ? '#38a169' : '#e53e3e' }}></div>
          </div>
        </div>

        {/* TILE 5: Avg Duration Variance - Type C */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: parseFloat(kpiStats.avgVarianceTypeC) <= 0 ? '#38a169' : '#e53e3e' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Avg Variance - Type C</div>
            <Tooltip title="Formula: AVG(Difference) for Type-C cleanings. Negative value = completed faster than planned. Positive value = took longer than planned. Based on completed cleanings only.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={{ ...styles.kpiValue, color: parseFloat(kpiStats.avgVarianceTypeC) <= 0 ? '#38a169' : '#e53e3e' }}>
            {parseFloat(kpiStats.avgVarianceTypeC) > 0 ? '+' : ''}{kpiStats.avgVarianceTypeC} hrs
          </div>
          <div style={styles.kpiSubtitle}>Based on {kpiStats.typeCCount} Type-C cleanings</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '100%', background: parseFloat(kpiStats.avgVarianceTypeC) <= 0 ? '#38a169' : '#e53e3e' }}></div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h2 style={styles.tableTitle}>Cleaning Activity Details</h2>
          <div style={styles.tableActions}>
            <Button style={styles.btnSecondary} icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              🔄 Refresh
            </Button>
            <Button style={styles.btnSecondary} icon={<DownloadOutlined />} onClick={handleExportExcel} loading={exporting}>
              📊 Export to Excel
            </Button>
          </div>
        </div>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
            scroll={{ x: 800, y: 500 }}
            sticky={{ offsetHeader: 0 }}
            size="middle"
            locale={{ emptyText: 'No cleaning activities found for the selected date range' }}
            style={{ fontSize: '13px' }}
          />
        </Spin>
      </div>

      {/* Add keyframes for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=JetBrains+Mono:wght@400;600&display=swap');
        
        .ant-table-thead > tr > th {
          position: sticky !important;
          top: 0 !important;
          z-index: 100 !important;
          background: #fafafa !important;
        }
        .ant-table-header {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
    </div>
  );
};

export default CleaningTimeVariance;
